/**
 * One-shot: add the Rate Watch pane to saved "Macro" workspaces that were
 * seeded before it existed. Idempotent. Flagged by
 * `mpp:migrate:macro-rate-watch:v1`.
 *
 * The page seed is idempotent by NAME, so a Macro workspace created earlier
 * never picks up a new component id on its own — without this, the pane would
 * only ever appear for someone who had not visited /timing/macro yet.
 *
 * Rate Watch is a wide board (meeting tabs, a bar chart and a history table),
 * so it goes in as a full-width row UNDER whatever is already arranged rather
 * than squeezing into the FRED grid. Trees that already contain it are left
 * alone — a user who removed it stays removed once the flag is set.
 */
import { safeLocalStorageGet, safeLocalStorageSet } from "@/lib/safeLocalStorage";
import type { WsNode, WorkspacePage } from "@/lib/workspace/workspacePages";

const FLAG = "mpp:migrate:macro-rate-watch:v1";
const LIST_KEY = "mpp:workspaces:v1";
const PAGE_NAME = "macro";
const COMPONENT = "rate:rateWatch";
/** Display-Profile namespace separator used by per-machine layout keys. */
const DP_SEP = "\u0000@dp:";

function nid(prefix: string): string {
  return `${prefix}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

function treeHas(node: WsNode, id: string): boolean {
  if (node.t === "leaf") return node.component === id;
  return node.children.some((c) => treeHas(c, id));
}

function inject(root: WsNode): WsNode {
  const leaf: WsNode = { id: nid("l"), t: "leaf", component: COMPONENT };
  // Already a vertical stack? Append a row rather than nesting another split.
  if (root.t === "split" && root.dir === "v") {
    return { ...root, children: [...root.children, leaf] };
  }
  return { id: nid("s"), t: "split", dir: "v", children: [root, leaf] };
}

function rewriteTreeJson(raw: string): string | null {
  try {
    const root = JSON.parse(raw) as WsNode;
    if (!root || (root.t !== "leaf" && root.t !== "split")) return null;
    if (treeHas(root, COMPONENT)) return null;
    return JSON.stringify(inject(root));
  } catch {
    return null;
  }
}

export function migrateMacroRateWatch(): void {
  if (typeof window === "undefined") return;
  if (safeLocalStorageGet(FLAG) === "1") return;

  let pool: WorkspacePage[] = [];
  try {
    const raw = safeLocalStorageGet(LIST_KEY);
    if (raw) {
      const arr = JSON.parse(raw) as WorkspacePage[];
      if (Array.isArray(arr)) pool = arr;
    }
  } catch {
    pool = [];
  }

  const macroIds = new Set<string>();
  let poolChanged = false;
  const nextPool: WorkspacePage[] = pool.map((p) => {
    if (!p?.root) return p;
    if (typeof p.name !== "string" || p.name.trim().toLowerCase() !== PAGE_NAME) return p;
    macroIds.add(p.id);
    if (treeHas(p.root, COMPONENT)) return p;
    poolChanged = true;
    return { ...p, root: inject(p.root), updatedAt: Date.now() };
  });
  if (poolChanged) safeLocalStorageSet(LIST_KEY, JSON.stringify(nextPool));

  // Per-machine / per-profile tree overrides hold their own copy of the
  // arrangement; a pool-only rewrite would leave those screens without the pane.
  try {
    const writes: Array<{ key: string; value: string }> = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (!key || !key.includes("mpp:ws-tree:")) continue;
      if (![...macroIds].some((id) => key.includes(id))) continue;
      const raw = window.localStorage.getItem(key);
      if (!raw) continue;
      const next = rewriteTreeJson(raw);
      if (next) writes.push({ key, value: next });
    }
    for (const { key, value } of writes) {
      safeLocalStorageSet(key.includes(DP_SEP) ? key.split(DP_SEP)[0]! : key, value);
      if (key.includes(DP_SEP)) {
        try {
          window.localStorage.setItem(key, value);
        } catch {
          /* quota — the logical write above is the one that matters */
        }
      }
    }
  } catch {
    /* pool migration still stands */
  }

  safeLocalStorageSet(FLAG, "1");
}
